import { Suspense, useMemo } from 'react';
import { Link, Outlet } from 'react-router';
import { useAuthMe, useLogout } from '../api/queries';
import { useConsentManager } from './CookieConsentBanner';
import { ImpersonationBanner } from './ImpersonationBanner';

/**
 * Read at module mount: was the visitor signed in last time we set a cookie?
 * Companion to the HttpOnly gmt.auth cookie — gmt.signedin is non-HttpOnly so JS
 * can read it synchronously and render the correct header nav on first paint,
 * dodging the flash-of-Sign-in-button that useAuthMe's network round-trip causes.
 *
 * Tamperable, but doesn't matter: an attacker who fakes the cookie just sees a
 * Dashboard link that 401s the moment they click it.
 */
function readSignedInHint(): boolean {
  if (typeof document === 'undefined') return false;
  return document.cookie
    .split(';')
    .some((c) => c.trim().startsWith('gmt.signedin=1'));
}

export function SiteShell() {
  const me = useAuthMe();
  const logout = useLogout();
  const consent = useConsentManager();
  const wasSignedIn = useMemo(readSignedInHint, []);

  // Decide which header nav to render. Once the real /me call resolves we trust it
  // entirely — the hint only fills the gap during the initial load.
  const showAuthed = me.data ? true : me.isLoading ? wasSignedIn : false;

  return (
    <div className="site">
      <ImpersonationBanner />
      <header className="site-header">
        <div className="inner">
          <Link to="/" className="brand" aria-label="Guess My Time home">
            <img className="brand-mark" src="/favicon.svg" alt="" width={28} height={28} />
            <span>Guess My Time</span>
          </Link>
          <nav className="right" aria-label="Account">
            {showAuthed ? (
              <>
                <Link className="nav-link nav-link-quiet" to="/dashboard">Dashboard</Link>
                <span className="nav-divider" aria-hidden="true">·</span>
                <button
                  type="button"
                  className="nav-link nav-link-quiet"
                  onClick={() => logout.mutate()}
                  disabled={logout.isPending}
                >
                  Sign out
                </button>
              </>
            ) : (
              <Link className="nav-link nav-link-primary" to="/sign-in">Sign in</Link>
            )}
          </nav>
        </div>
      </header>
      <main className="site-main">
        <Suspense fallback={<p>Loading…</p>}>
          <Outlet />
        </Suspense>
      </main>
      <footer className="site-footer">
        <div className="inner">
          <div className="footer-attribution">
            <span>
              Run by <a href="https://webhooks.technology" target="_blank" rel="noopener"><strong>Webhooks Ltd</strong></a>,
              a UK software consultancy.
            </span>
            <span className="footer-disclosure">
              Donations are never processed here; they go directly to the fundraiser's chosen page.
              No purchase necessary — entry is free.
            </span>
          </div>
          <nav className="footer-links" aria-label="Footer">
            <a href="/how-it-works">How it works</a>
            <a href="/charities">For charities</a>
            <a href="/faq">FAQ</a>
            <a href="/company">Company</a>
            <Link to="/privacy">Privacy</Link>
            <Link to="/terms">Terms</Link>
            <button
              type="button"
              className="link-button footer-link-button"
              onClick={() => consent.open()}
            >
              Manage cookies
            </button>
          </nav>
        </div>
      </footer>
    </div>
  );
}
